import React from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { HiOutlineSquares2X2 } from 'react-icons/hi2';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { LISTING_STATE_CLOSED } from '../../util/types';
import { ResponsiveImage, Modal, ImageCarousel } from '../../components';
import ActionBarMaybe from './ActionBarMaybe';

import css from './SectionImages.css';

// Scaled variants keep the uploaded 16:9 crop; the cells crop with object-fit.
const GALLERY_VARIANTS = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];

// The gallery shows at most three images: one large and two stacked (Figma 313:1868).
const MAX_GALLERY_IMAGES = 3;

const sizesFor = (count, index) => {
  if (count === 1) {
    return '(max-width: 1160px) 100vw, 1128px';
  }
  if (count === 2) {
    return index === 0 ? '(max-width: 961px) 100vw, 558px' : '558px';
  }
  return index === 0 ? '(max-width: 961px) 100vw, 746px' : '370px';
};

const SectionImages = props => {
  const {
    title,
    listing,
    isOwnListing,
    handleViewPhotosClick,
    imageCarouselOpen,
    onImageCarouselClose,
    onManageDisableScrolling,
    intl,
  } = props;

  const images = listing.images || [];
  const shownImages = images.slice(0, MAX_GALLERY_IMAGES);
  const count = shownImages.length;

  const isClosed = listing.attributes?.state === LISTING_STATE_CLOSED;
  // Owners see the ListingOwnerBanner instead; visitors of a closed listing
  // get the closed notice over the gallery.
  const hasActionBar = !!listing.id && isClosed && !isOwnListing;
  const actionBar = hasActionBar ? <ActionBarMaybe listing={listing} /> : null;

  if (count === 0) {
    // No empty frame: only the owner's (or closed listing's) bar, when there is one.
    return actionBar ? <div className={css.actionBarWithoutImages}>{actionBar}</div> : null;
  }

  // One image is the base layout (full width, 16:9).
  const layoutClass = count === 2 ? css.double : count > 2 ? css.triple : null;

  // Desktop shows every gallery image, so "Show all" is needed there only for 3+.
  // Mobile shows the first image only, so it is needed for 2+.
  const showAllButton =
    images.length > 1 ? (
      <button
        type="button"
        className={classNames(css.showAll, { [css.showAllMobileOnly]: count === 2 })}
        onClick={handleViewPhotosClick}
      >
        <HiOutlineSquares2X2 className={css.showAllIcon} aria-hidden="true" />
        <FormattedMessage id="SectionImages.showAll" />
      </button>
    ) : null;

  return (
    <div className={css.root}>
      <div className={classNames(css.gallery, layoutClass)}>
        {shownImages.map((image, index) => (
          <div
            key={image.id ? image.id.uuid : index}
            className={classNames(css.cell, index === 0 ? css.mainCell : css.sideCell)}
          >
            <button type="button" className={css.imageButton} onClick={handleViewPhotosClick}>
              <ResponsiveImage
                rootClassName={css.image}
                alt={
                  index === 0
                    ? title
                    : intl.formatMessage(
                        { id: 'SectionImages.imageAlt' },
                        { title, index: index + 1 }
                      )
                }
                image={image}
                variants={GALLERY_VARIANTS}
                sizes={sizesFor(count, index)}
              />
            </button>
            {index === 0 ? actionBar : null}
          </div>
        ))}
        {showAllButton}
      </div>
      <Modal
        id="ListingPage.imageCarousel"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={imageCarouselOpen}
        onClose={onImageCarouselClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel images={images} />
      </Modal>
    </div>
  );
};

SectionImages.defaultProps = {
  title: '',
  isOwnListing: false,
  imageCarouselOpen: false,
};

SectionImages.propTypes = {
  title: string,
  listing: object.isRequired,
  isOwnListing: bool,
  handleViewPhotosClick: func.isRequired,
  imageCarouselOpen: bool,
  onImageCarouselClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SectionImages);
